// Файл: src/components/EventDetailsDialog.js

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Chip, Avatar, AvatarGroup, Tooltip, IconButton, List, ListItem, ListItemIcon, ListItemText, alpha
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkIcon from '@mui/icons-material/Link';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';

const eventTypeMap = (t) => ({
  meeting: { label: t('event_details.type.meeting'), color: 'primary' },
  call: { label: t('event_details.type.call'), color: 'secondary' },
  visit: { label: t('event_details.type.visit'), color: 'success' },
  assembly: { label: t('event_details.type.assembly'), color: 'info' },
});

export const EventDetailsDialog = ({ event, onClose }) => {
  const { t } = useTranslation();
  if (!event) return null;

  const duration = Math.round((new Date(event.end) - new Date(event.start)) / 60000);
  const typeInfo = eventTypeMap(t)[event.type] || { label: event.type, color: 'default' };

  return (
    <Dialog 
      open={true} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2,
        background: (theme) => theme.palette.mode === 'dark'
          ? alpha(theme.palette[typeInfo.color]?.main || theme.palette.primary.main, 0.15)
          : alpha(theme.palette[typeInfo.color]?.main || theme.palette.primary.main, 0.08),
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip 
              label={typeInfo.label} 
              color={typeInfo.color} 
              size="medium"
              sx={{ fontWeight: 600 }}
            />
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              {event.title}
            </Typography>
        </Box>
        <IconButton 
          onClick={onClose}
          sx={{
            '&:hover': {
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
              color: 'error.main',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        {event.description && (
          <Typography 
            paragraph 
            sx={{ 
              mb: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: (theme) => theme.palette.mode === 'dark' 
                ? alpha(theme.palette.common.white, 0.03)
                : alpha(theme.palette.primary.main, 0.03),
              fontStyle: 'italic',
            }}
          >
            {event.description}
          </Typography>
        )}
        
        <List sx={{ p: 0 }}>
            <ListItem sx={{ 
              borderRadius: 1.5,
              mb: 1,
              '&:hover': {
                bgcolor: (theme) => theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.common.white, 0.05)
                  : alpha(theme.palette.primary.main, 0.05),
              }
            }}>
                <ListItemIcon><AccessTimeIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary={<Typography variant="body2" fontWeight={600}>{t('event_details.time')}</Typography>}
                  secondary={`${new Date(event.start).toLocaleString()} - ${new Date(event.end).toLocaleTimeString()} (${duration} ${t('event_details.min')})`} 
                />
            </ListItem>
            {event.location && <ListItem sx={{ 
              borderRadius: 1.5,
              mb: 1,
              '&:hover': {
                bgcolor: (theme) => theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.common.white, 0.05)
                  : alpha(theme.palette.primary.main, 0.05),
              }
            }}>
                <ListItemIcon><LocationOnIcon color="error" /></ListItemIcon>
                <ListItemText 
                  primary={<Typography variant="body2" fontWeight={600}>{t('event_details.location')}</Typography>}
                  secondary={event.location} 
                />
            </ListItem>}
            {event.callLink && <ListItem sx={{ 
              borderRadius: 1.5,
              mb: 1,
              '&:hover': {
                bgcolor: (theme) => theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.common.white, 0.05)
                  : alpha(theme.palette.primary.main, 0.05),
              }
            }}>
                <ListItemIcon><LinkIcon color="info" /></ListItemIcon>
                <ListItemText 
                  primary={<Typography variant="body2" fontWeight={600}>{t('event_details.call_link')}</Typography>}
                  secondary={
                    <a 
                      href={event.callLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: 'inherit',
                        textDecoration: 'underline',
                        fontWeight: 500,
                      }}
                    >
                      {event.callLink}
                    </a>
                  } 
                />
            </ListItem>}
            {event.responsibles?.length > 0 && <ListItem sx={{ 
              borderRadius: 1.5,
              '&:hover': {
                bgcolor: (theme) => theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.common.white, 0.05)
                  : alpha(theme.palette.primary.main, 0.05),
              }
            }}>
                <ListItemIcon><PeopleIcon color="success" /></ListItemIcon>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {t('event_details.participants')}
                  </Typography>
                  <AvatarGroup max={4} sx={{ ml: 2 }}>
                      {event.responsibles.map(user => (
                          <Tooltip title={user.name} key={user._id}>
                              <Avatar 
                                alt={user.name} 
                                src={user.avatarUrl}
                                sx={{ 
                                  width: 36, 
                                  height: 36,
                                  border: (theme) => `2px solid ${theme.palette.background.paper}`,
                                }}
                              >
                                {user.name[0]}
                              </Avatar>
                          </Tooltip>
                      ))}
                  </AvatarGroup>
                </Box>
            </ListItem>}
        </List>

      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{ px: 3, fontWeight: 600 }}
        >
          {t('event_details.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};